import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  CardContent,
  CardDescription,
  CardFooter,
  DateTimePickerWithRange,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  LoadingSpinner,
  ScrollArea,
  TruncatedText,
} from '@ais-ui/ui-core';
import React, { useMemo, useState } from 'react';
import { cn, isNonEmpty, useTimeZoneContext } from '@ais-ui/utils';
import { v4 as uuidv4 } from 'uuid';
import {
  PiArrowRightThin,
  PiBroadcastThin,
  PiChatsCircleThin,
  PiDotsThreeVertical,
} from 'react-icons/pi';
import { useLocalization } from '@ais-ui/locale-management';
import { SentMessageWithDate, useMessagesSentList } from '@ais-ui/api';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { DateRange } from 'react-day-picker';
import { filterSentMessages } from './utils';
import { useSelectMessageTargets } from '../../../../../libs/websockets/src/lib/ol-server/useSelectMessageTargets';
import {
  AutoCompleteShipInfo,
  useGetEvents,
  useGetEventsForPeriod,
} from '@ais-ui/websockets';
import { useAISMapContext } from '../Map/AISMapContext';
import { useZoomAndSelectShip } from '../Map/useZoomAndSelectShip';
import { NoItemsEmptyStateList } from '../../components/EmptyList';
import { MessageActionsMenu } from './MessageActionsMenu';

interface SentMessageGridItemProps {
  label: string;
  value: string | undefined | null;
}
const SentMessageGridItem = ({ label, value }: SentMessageGridItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-muted-foreground flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : '-'}
      </p>
    </div>
  );
};

interface SenderInfoProps {
  sentMessage: SentMessageWithDate;
}

const SenderInfo = ({ sentMessage }: SenderInfoProps) => {
  useGetEvents();
  const now = useMemo(() => new Date(), []);
  const fromTime = useMemo(() => {
    if (!sentMessage.time) {
      return;
    }

    return new Date(sentMessage.time.getTime() - 60 * 60 * 1000);
  }, [sentMessage]);

  const {
    data: eventData,
    isLoading,
    isFetching,
  } = useGetEventsForPeriod(fromTime, now);

  const matchingEvent = useMemo(() => {
    if (!sentMessage?.eventId || !eventData?.events) {
      return null;
    }

    return (
      eventData.events.find((event) => event.id === sentMessage.eventId) || null
    );
  }, [sentMessage?.eventId, eventData?.events]);

  return (
    <>
      <Label className="text-lg">Sender</Label>
      <div className="grid grid-cols-2 gap-4">
        <SentMessageGridItem
          label="MMSI"
          value={
            sentMessage?.source?.mmsi ? sentMessage.source.mmsi.toString() : ''
          }
        />
        <SentMessageGridItem
          label="Event detector"
          value={matchingEvent?.name ? matchingEvent.name : ''}
        />
      </div>
    </>
  );
};

interface ReceiverInfoProps {
  sentMessage: SentMessageWithDate;
}

const ReceiverInfo = ({ sentMessage }: ReceiverInfoProps) => {
  return (
    <>
      <Label className="text-lg">Receiver</Label>
      <div className="grid grid-cols-2 gap-4">
        <SentMessageGridItem
          label="MMSI"
          value={
            sentMessage?.destination?.mmsi
              ? sentMessage.destination.mmsi.toString()
              : ''
          }
        />
      </div>
    </>
  );
};

interface SentMessageItemProps {
  sentMessage: SentMessageWithDate;
}

const SentMessageDropDownMenu = ({
  sentMessage,
  className,
}: {
  sentMessage: SentMessageWithDate;
  className?: string;
}) => {
  const { messages } = useLocalization();
  const selectMessageTarget = useSelectMessageTargets();

  const { setZoomToShip } = useAISMapContext();

  const onShow = () => {
    const receiverMmsi = sentMessage?.destination?.mmsi
      ? sentMessage.destination.mmsi
      : -1;
    const senderMmsi = sentMessage?.source?.mmsi ? sentMessage.source.mmsi : -1;

    selectMessageTarget(senderMmsi, receiverMmsi);

    const newSelectedShip: AutoCompleteShipInfo = {
      callSign: '',
      imo: null,
      mmsi: sentMessage?.source?.mmsi
        ? sentMessage.source.mmsi.toString()
        : null,
      name: null,
      type: 'TT_SHIP',
    };

    setZoomToShip(newSelectedShip);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className={className}>
        <Button size="icon" variant="ghost" className="size-7">
          <PiDotsThreeVertical className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onShow}>Show</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const SentMessageItem = ({ sentMessage }: SentMessageItemProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const addressedMessageText = `${sentMessage?.source?.mmsi} → ${sentMessage?.destination?.mmsi}`;

  const broadcastMessage = `Broadcast by ${sentMessage?.source?.mmsi}`;

  return (
    <AccordionItem
      key={sentMessage.id}
      value={sentMessage.id ? sentMessage.id : uuidv4()}
      className={cn('px-4')}
    >
      <div className="flex justify-between items-center">
        <div className="flex-grow">
          <AccordionTrigger className="w-full py-2" hideChevron={true}>
            <div className="flex items-center min-w-0 w-full">
              <div className="relative overflow-visible flex-shrink-0">
                {sentMessage.addressed && (
                  <PiChatsCircleThin className="h-6 w-6" />
                )}
                {!sentMessage.addressed && (
                  <PiBroadcastThin className="h-6 w-6" />
                )}
              </div>
              <div className="ml-4 space-y-1 min-w-0 flex-1">
                <TruncatedText
                  text={
                    sentMessage.addressed
                      ? addressedMessageText
                      : broadcastMessage
                  }
                  className="text-sm text-left font-medium leading-none group-hover:underline"
                  maxWidth="224px"
                />
                <p className="text-sm text-left text-muted-foreground truncate">
                  {formatDateTimeWithTimeZone(sentMessage.time)}
                </p>
              </div>
            </div>
          </AccordionTrigger>
        </div>
        <SentMessageDropDownMenu
          sentMessage={sentMessage}
        ></SentMessageDropDownMenu>
      </div>

      <AccordionContent>
        <div className="grid gap-6 px-4 pt-4">
          <div className="grid gap-4">
            <SenderInfo sentMessage={sentMessage}></SenderInfo>
            {sentMessage.addressed && (
              <ReceiverInfo sentMessage={sentMessage}></ReceiverInfo>
            )}
            <SentMessageGridItem label="Text" value={sentMessage.text} />
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export const SentMessages = () => {
  const { messages } = useLocalization();
  const [search, setSearch] = useState<string>('');

  const [date, setDate] = React.useState<DateRange | undefined>({
    from: new Date(new Date().getTime() - 1 * 60 * 60 * 1000),
    to: new Date(),
  });

  const { data, isLoading } = useMessagesSentList(date?.from, date?.to);
  const { timeZone } = useTimeZoneContext();
  const { userDateFormat } = useDateTimeFormat();

  const sentMessages = data?.records || [];

  const filteredSentMessages = useMemo(() => {
    if (!data) {
      return [];
    }

    const sentMessages = data.records || [];
    if (search === '') {
      return sentMessages;
    }

    return filterSentMessages(sentMessages, search);
  }, [data, search]);

  return (
    <>
      <CardContent className="space-y-4">
        <CardDescription>{messages.messagesSubtitle}</CardDescription>
        <DateTimePickerWithRange
          className="w-full"
          date={date}
          setDate={setDate}
          timeZone={timeZone}
          dateTimeFormat={userDateFormat}
          dateLimit="past"
        ></DateTimePickerWithRange>
        <Input
          id="search"
          placeholder={messages.messagesSearchMessages}
          className="h-10"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />
      </CardContent>
      <CardContent className="flex-1 flex flex-col overflow-hidden">
        {!isLoading && sentMessages?.length === 0 && (
          <NoItemsEmptyStateList
            entity="sent message"
            srcImage="empty_list_bro.png"
          ></NoItemsEmptyStateList>
        )}
        {isLoading && <LoadingSpinner></LoadingSpinner>}
        {!isLoading && sentMessages?.length !== 0 && (
          <Accordion
            className="flex-1 flex flex-col overflow-hidden"
            type="single"
            collapsible
          >
            <ScrollArea
              className="flex-grow p-0 overflow-y-auto border rounded-lg"
              observeResize={true}
            >
              {filteredSentMessages.map((sentMessage) => {
                return (
                  <SentMessageItem
                    key={sentMessage.id}
                    sentMessage={sentMessage}
                  />
                );
              })}
            </ScrollArea>
          </Accordion>
        )}
      </CardContent>
      <CardFooter className="flex flex-grow-0 flex-col items-end">
        <MessageActionsMenu buttonText="Actions" />
      </CardFooter>
    </>
  );
};
